import { ActionEvents } from '@griply/types';
import { useCallback } from 'react';
import { useRevalidator } from 'react-router-dom';
import { useCreatePushNotificationMutation } from 'shared/hooks/queries/use-create-push-notification-mutation';
import { useLoginUserMutation } from 'shared/hooks/queries/use-login-user-mutation';
import { useNotificationPermission } from 'shared/hooks/use-notification-permission';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { User } from 'shared/types/user';

export const useLogin = () => {
  const locale = useI18nLocale();
  const track = useTrackEvents();
  const { revalidate } = useRevalidator();
  const [permission] = useNotificationPermission();
  const { mutateAsync, isError, error, reset, isPending } =
    useLoginUserMutation();
  const { mutate: createPushToken } = useCreatePushNotificationMutation();

  const login = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      let user: User | null;
      try {
        user = await mutateAsync({ email, password });
      } catch {
        // the mutation's `error` state carries the form error; the submit handler has no catch
        return;
      }
      track(ActionEvents.OnboardingLogin, undefined);

      // if the browser already gave permission, we will set a push token for the user at login
      if (permission === 'granted' && user) {
        createPushToken({
          userId: user.uid,
          locale: user?.settings?.language ?? locale,
        });
      }

      revalidate();
    },
    [createPushToken, locale, mutateAsync, permission, revalidate, track],
  );

  return {
    login,
    isError,
    retry: reset,
    isLoading: isPending,
    error: error?.kind,
  };
};
